import type { Project, Task, TaskActivity } from '@portfolio-pm/shared';
import { useNow } from '../../../lib/useNow.ts';
import { ScrollViewport } from '../../layout/ScrollViewport.tsx';
import {
  addDays,
  formatCompactMinutes,
  formatMinutes,
  startOfDay,
  summarizeByProject,
  summarizeCompletions,
  summarizeTimeline,
} from './detail-utils.ts';

/**
 * Aggregated view shown in the inspector when no task is selected:
 * headline metrics plus project/timeline/completion charts.
 * Extracted from TaskDetailPanel.tsx.
 */
export function TaskViewSummaryPanel({
  tasks,
  completedCount,
  allTasks,
  taskActivities,
  projectsById,
  title,
  description,
}: {
  tasks: Task[];
  completedCount: number;
  allTasks: Task[];
  taskActivities: TaskActivity[];
  projectsById: Map<string, Project>;
  title: string;
  description: string;
}): JSX.Element {
  const now = useNow();
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  const dayAfterTomorrow = addDays(today, 2);
  const totalEstimate = tasks.reduce((sum, task) => sum + (task.estimateMinutes ?? 0), 0);
  const projectBreakdown = summarizeByProject(tasks, projectsById);
  const timeline = summarizeTimeline(tasks, today);
  const completions = summarizeCompletions(allTasks, taskActivities, today);

  const todayCount = tasks.filter(
    (task) => task.dueAt !== undefined && task.dueAt >= today && task.dueAt < tomorrow,
  ).length;
  const tomorrowCount = tasks.filter(
    (task) => task.dueAt !== undefined && task.dueAt >= tomorrow && task.dueAt < dayAfterTomorrow,
  ).length;
  const overdueCount = tasks.filter(
    (task) => task.completedAt === undefined && task.dueAt !== undefined && task.dueAt < today,
  ).length;

  return (
    <aside
      data-testid="task-detail-panel"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white"
    >
      <div data-testid="task-summary-header" className="pane-divider-b px-3 py-3">
        <div className="flex min-w-0 items-center">
          <h2 className="truncate text-base font-medium text-[color:var(--app-text)]">{title}</h2>
        </div>
        <p
          className="mt-0.5 truncate text-xs text-[color:var(--app-text-soft)]"
          title={description}
        >
          {description}
        </p>
        <div className="mt-3 grid grid-cols-[minmax(38px,0.65fr)_minmax(74px,1.35fr)_repeat(4,minmax(48px,1fr))] gap-1">
          <SummaryMetric label="tasks" value={String(tasks.length)} />
          <SummaryMetric label="estimated" value={formatCompactMinutes(totalEstimate)} />
          <SummaryMetric label="due today" value={String(todayCount)} />
          <SummaryMetric label="due tomorrow" value={String(tomorrowCount)} />
          <SummaryMetric label="overdue" value={String(overdueCount)} />
          <SummaryMetric label="completed" value={String(completedCount)} />
        </div>
      </div>

      <ScrollViewport
        className="min-h-0 flex-1"
        viewportClassName="task-detail-viewport h-full px-4"
      >
        <div className="space-y-5" data-testid="task-view-summary">
          <SummaryChart title="Project Breakdown">
            {projectBreakdown.length === 0 ? (
              <p className="surface-empty p-4">No project volume yet.</p>
            ) : (
              projectBreakdown.map((entry) => (
                <HorizontalBar
                  key={entry.projectId}
                  label={entry.label}
                  value={`${entry.count} (${entry.percent}%)`}
                  percent={entry.percent}
                />
              ))
            )}
          </SummaryChart>

          <SummaryChart title="Due Timeline">
            {timeline.every((entry) => entry.count === 0) ? (
              <p className="surface-empty p-4">No scheduled or due tasks in the next week.</p>
            ) : (
              <div className="space-y-2">
                {timeline.map((entry) => (
                  <HorizontalBar
                    key={entry.label}
                    label={entry.label}
                    value={`${entry.count} / ${formatMinutes(entry.effort)}`}
                    percent={entry.percent}
                  />
                ))}
              </div>
            )}
          </SummaryChart>

          <SummaryChart title="Recent Completions">
            {completions.every((entry) => entry.count === 0) ? (
              <p className="surface-empty p-4">No completions recorded in the last week.</p>
            ) : (
              <div className="grid grid-cols-7 items-end gap-2" data-testid="completion-chart">
                {completions.map((entry) => (
                  <div key={entry.label} className="space-y-1 text-center">
                    <div className="flex h-20 items-end rounded-lg bg-white/60 p-1">
                      <div
                        className="w-full rounded bg-[color:var(--app-success)]"
                        style={{ height: `${Math.max(8, entry.percent)}%` }}
                      />
                    </div>
                    <p className="text-[10px] font-medium text-[color:var(--app-text-soft)]">
                      {entry.label}
                    </p>
                    <p className="text-xs text-[color:var(--app-text-muted)]">{entry.count}</p>
                  </div>
                ))}
              </div>
            )}
          </SummaryChart>
        </div>
      </ScrollViewport>
    </aside>
  );
}

function SummaryMetric({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="min-w-0 rounded-md bg-[color:var(--app-panel-muted)] px-2 py-2">
      <p className="truncate text-base font-semibold leading-5 text-[color:var(--app-text)]">
        {value}
      </p>
      <p className="mt-0.5 min-h-6 text-[10px] leading-3 text-[color:var(--app-text-muted)]">
        {label}
      </p>
    </div>
  );
}

function SummaryChart({
  title,
  children,
}: {
  title: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <section className="surface-panel-muted p-3">
      <h3 className="ui-label">{title}</h3>
      <div className="mt-3 space-y-2">{children}</div>
    </section>
  );
}

function HorizontalBar({
  label,
  value,
  percent,
}: {
  label: string;
  value: string;
  percent: number;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
        <span className="truncate font-medium text-[color:var(--app-text)]">{label}</span>
        <span className="shrink-0 text-[color:var(--app-text-muted)]">{value}</span>
      </div>
      <div className="progress-track h-1.5">
        <div className="progress-fill" style={{ width: `${Math.max(3, percent)}%` }} />
      </div>
    </div>
  );
}
